import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {Box, useMediaQuery} from "@mui/material";
import {decomposeColor, getContrastRatio, useTheme} from "@mui/material/styles";
import {LottieLight, LottieLightProps} from "lottie-react";
import React, {useMemo} from "react";
import signingSuccessAnimation from "./assets/signingSuccess.json";
import {compositeColor} from "./signingColors";
import {LottieRgb, recolorLottie} from "./lottieRecolor.util";

const LottiePlayer = LottieLight as React.ComponentType<LottieLightProps>;

const CIRCLE = '#27A360';
const LIGHT_CONFETTI = '#47AD7D';
const SHINE = '#C4D2FF';
const MIN_CHECK_CONTRAST = 3;

const toRgb = (color: string): LottieRgb => (
    decomposeColor(color).values.slice(0, 3).map(channel => channel / 255) as LottieRgb
);

const withWhite = (color: LottieRgb, amount: number): LottieRgb => (
    color.map(channel => channel * (1 - amount) + amount) as LottieRgb
);

function SigningSuccessAnimation() {
    const theme = useTheme();
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', {noSsr: true});

    const primary = compositeColor(theme.palette.primary.main, '#FFFFFF');
    const circle = getContrastRatio('#FFFFFF', primary) >= MIN_CHECK_CONTRAST ? primary : CIRCLE;

    const animationData = useMemo(() => {
        const rgb = toRgb(circle);
        return recolorLottie(signingSuccessAnimation, {
            [CIRCLE]: rgb,
            [LIGHT_CONFETTI]: withWhite(rgb, 0.35),
            [SHINE]: withWhite(rgb, 0.75),
        });
    }, [circle]);

    if (prefersReducedMotion) {
        return <CheckCircleIcon aria-hidden sx={{fontSize: 96, color: circle, mx: 'auto'}}/>;
    }

    return (
        <Box aria-hidden sx={{width: {xs: 220, sm: 272}, height: {xs: 220, sm: 272}, mx: 'auto', my: {xs: -3, sm: -4}}}>
            <LottiePlayer
                src={animationData}
                autoplay
                loop={false}
                style={{width: '100%', height: '100%'}}
            />
        </Box>
    );
}

export default SigningSuccessAnimation;
